import ModuleContext from 'CMFW/context/ModuleContext';
import Grid from 'CMFW/grid/Grid';
import {
  ContainerLeft,
  ContainerLeftHeader,
  ContainerRight,
  EditorRow,
  ModuleContainer,
} from 'components/common/styles';
import { Split } from 'components/Split';
import { useForceRender } from 'hooks/useForceRender';
import React, { useMemo, useState } from 'react';
import { Button, TextField, IconButton } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import MemorySemanticUnifierModel from '../../../models/MemorySemanticUnifierModel';
import MemorySemanticUnifierConfig, { IMemorySemanticUnifierConfig } from '../components/MemorySemanticUnifierConfig';
import styled from 'styled-components';
import MemorySemanticUnifierItemsModel from '../../../models/MemorySemanticUnifierItemsModel';
import MemorySemanticUnifierBatchModel from '../../../models/MemorySemanticUnifierBatchModel';
import MemorySemanticUnifierBatch from '../components/MemorySemanticUnifierBatch';
import { IFields } from '../../../CMFW/grid/fields';

// Keep filter text outside the component so AG Grid callbacks
// always read the latest value across renders (same pattern as Pattern Consistency)
let msuFilterText = '';

const MemorySemanticUnifier: React.FC = () => {
  const forceRender = useForceRender();
  const [showResults, setShowResults] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');

  const moduleModel = useMemo(() => new MemorySemanticUnifierModel({ render: forceRender }), [forceRender]);
  const itemsModel = useMemo(() => new MemorySemanticUnifierItemsModel({ render: forceRender }), [forceRender]);
  const batchModel = useMemo(() => new MemorySemanticUnifierBatchModel({ render: forceRender, chunkSize: 100 }), [
    forceRender,
  ]);
  // Ensure items model knows about the parent module model
  itemsModel.setParentModel(moduleModel);

  const handleStartGuessing = async (config: IMemorySemanticUnifierConfig) => {
    setIsProcessing(true);
    try {
      itemsModel.reset();
      setShowResults(true);

      // Single flow: build once; if embeddings are needed, run batch then rebuild
      const buildOrRebuild = async () => {
        const res = await moduleModel.requestBuild(config);
        if (res?.status === 'needs_embeddings') {
          // Reset both grids when embeddings start
          moduleModel.reset(true);
          itemsModel.reset();
          batchModel.load(res.to_embed || []);
          await batchModel.start();
          // If user paused during upsert, do NOT trigger rebuild now; rebuild on completion
          if (batchModel.paused && batchModel.pending > 0) {
            batchModel.onComplete = async () => {
              const rebuilt = await moduleModel.requestBuild(config);
              if (rebuilt?.status === 'ok') {
                moduleModel.gridData = (rebuilt.groups || []) as any[];
                moduleModel.render();
              }
              // Hide batch widget after finishing
              batchModel.load([]);
            };
            return { status: 'paused', pending: batchModel.pending, done: batchModel.done, total: batchModel.total };
          }
          // Embedding completed now; hide batch and rebuild
          batchModel.load([]);
          return await moduleModel.requestBuild(config);
        }
        return res;
      };

      // Share dialect with items model for change operations
      itemsModel.setDialectId(config.general.source_dialect);
      const result = await buildOrRebuild();
      if (result?.status === 'ok') {
        moduleModel.gridData = (result.groups || []) as any[]; // shape already matches the grid
        moduleModel.render();
      } else if (result?.status === 'paused') {
        // Keep showing current grid (if any). Batch will trigger rebuild upon completion via onComplete.
      }
    } catch (error) {
      console.error('Error running semantic unifier:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToConfig = () => setShowResults(false);

  React.useEffect(() => {
    const h = setTimeout(() => setFilterText(filterInput), 300);
    return () => clearTimeout(h);
  }, [filterInput]);

  // Update module-level filter text so Grid callbacks see the latest
  msuFilterText = filterText || '';

  // External filter helpers for left grid (align with Pattern Consistency)
  const msuIsExternalFilterActive = () => (msuFilterText || '').trim() !== '';
  const msuDoesExternalFilterPass = (node: any) => {
    const t = (msuFilterText || '').trim();
    if (t === '') return true;
    let re: RegExp | null = null;
    try {
      re = new RegExp(t, 'i');
    } catch {
      re = null;
    }
    const data: any = node?.data || {};
    const rep = String(data.representative_pattern || '');
    const members: any[] = Array.isArray(data.members) ? data.members : [];
    const matchStr = (s: string) => (re ? re.test(s) : s.toLowerCase().includes(t.toLowerCase()));
    if (matchStr(rep)) return true;
    for (const m of members) {
      const mp = String(m?.pattern || '');
      if (matchStr(mp)) return true;
    }
    return false;
  };

  const cellRenderers = React.useMemo(
    () => ({
      [IFields.moduleNavigation]: 'multiNavigationRenderer',
      [IFields.actions]: 'msuChangeRenderer',
    }),
    [],
  );

  return (
    <ModuleContainer>
      <ModuleContext.Provider value={moduleModel}>
        <ContainerLeft>
          <ConfigContainer style={{ display: showResults ? 'none' : 'block' }}>
            <MemorySemanticUnifierConfig onStartGuessing={handleStartGuessing} />
          </ConfigContainer>
          <ResultsContainer style={{ display: showResults ? 'block' : 'none' }}>
            <Split widthLeft={600}>
              <ContainerLeft>
                <ContainerLeftHeader>
                  <EditorRowStart>
                    <Button variant="outlined" size="small" onClick={handleBackToConfig} disabled={isProcessing}>
                      Back to Configuration
                    </Button>
                    <ResultsInfo>{moduleModel.gridData?.length || 0} groups</ResultsInfo>
                    <FilterRow>
                      <TextFieldStyled
                        variant="outlined"
                        size="small"
                        placeholder="Filter groups"
                        value={filterInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterInput(e.target.value)}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          setFilterInput('');
                          setFilterText('');
                        }}
                        disabled={!filterInput}
                        title="Clear"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </IconButton>
                    </FilterRow>
                    <MemorySemanticUnifierBatch batchModel={batchModel} />
                  </EditorRowStart>
                </ContainerLeftHeader>
                <Grid
                  isFilterActive={() => msuIsExternalFilterActive()}
                  isRowVisible={msuDoesExternalFilterPass}
                  handleRowClick={(data: any) => {
                    const gid = data?.id;
                    const freshGroup = Array.isArray(moduleModel.gridData)
                      ? (moduleModel.gridData as any).find((g: any) => g && g.id === gid)
                      : null;
                    const groupObj = freshGroup || data;
                    const members = Array.isArray(groupObj?.members) ? groupObj.members : [];
                    itemsModel.setCurrentGroup(groupObj);
                    itemsModel.setMembers(members);
                  }}
                />
              </ContainerLeft>
              <ContainerRight>
                <ModuleContext.Provider value={itemsModel}>
                  <Grid cellRenderers={cellRenderers} disableRowLoadFields={[IFields.actions]} />
                </ModuleContext.Provider>
              </ContainerRight>
            </Split>
          </ResultsContainer>
        </ContainerLeft>
      </ModuleContext.Provider>
    </ModuleContainer>
  );
};

export default MemorySemanticUnifier;

const EditorRowStart = styled(EditorRow)`
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
`;

const ConfigContainer = styled.div`
  height: 100%;
  overflow: hidden;
`;

const ResultsContainer = styled.div`
  height: 100%;
  overflow: hidden;
`;

const ResultsInfo = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const TextFieldStyled = styled(TextField)`
  flex: 1;
`;
